import { ApiClientService, EntityApiService, SearchResponseType } from '@dfl/react-security';
import { IAddress } from '../interfaces';
import { MS_LOCATION_CONFIG } from '../settings/address-location';

const addConfig = (config: any) => {
  return {
    ...config,
    headers: MS_LOCATION_CONFIG?.headers || {},
    ignoreSpace: true,
    ignoreToken: true,
  };
};

class AddressService extends EntityApiService<IAddress> {
  searchState = (params: {
    search: string;
    page: number;
    size: number;
    filter: object;
    sort: object;
  }): Promise<any> => {
    return this.handleSearchResponse(
      ApiClientService.get(
        this.getPath(params?.search ? `/provinces?q=${params?.search}` : '/provinces'),
        addConfig({}),
      ),
      10,
    );
  };

  searchCity = (state: string, search?: string): Promise<SearchResponseType<any>> => {
    if (state) {
      return this.handleSearchResponse(
        ApiClientService.get(
          this.getPath(
            search ? `/provinces/${state}/municipalities?q=${search}` : `/provinces/${state}/municipalities`,
          ),
          addConfig({}),
        ),
        10,
      );
    }
    return Promise.reject(new Error('You must need a state code'));
  };

  searchAddress1 = (params: {
    province: string;
    municipality: string;
    size: number;
    search?: string;
  }): Promise<SearchResponseType<any>> => {
    return this.handleSearchResponse(
      ApiClientService.get(
        this.getPath(
          `/provinces/${params?.province}/municipalities/${params?.municipality}/streets?q=${params?.search || ''}`,
        ),
        addConfig({}),
      ),
      params?.size || 10,
    )?.then((res) => {
      return {
        ...res,
        hasMore: true,
      };
    });
  };

  searchAddress2 = async (params: {
    province: string;
    municipality: string;
    mainStreet: string;
    size: number;
    search?: string;
  }): Promise<SearchResponseType<any>> => {
    return await this.handleSearchResponse(
      ApiClientService.get(
        this.getPath(
          `/provinces/${params?.province}/municipalities/${params?.municipality}/streets/${
            params?.mainStreet
          }/between-streets?q=${params?.search || ''}`,
        ),
        addConfig({}),
      ),
      params?.size || 10,
    )?.then((res) => {
      return {
        ...res,
        hasMore: true,
      };
    });
  };

  /* get one state */
  getOneState = (code: string): Promise<any> => {
    return this.handleResponse(ApiClientService.get(this.getPath(`/provinces/${code}`), addConfig({})));
  };

  /* get one state */
  getOneCity = (state: string, code: string): Promise<any> => {
    if (state && code) {
      return this.handleResponse(
        ApiClientService.get(this.getPath(`/provinces/${state}/municipalities/${code}`), addConfig({})),
      );
    }
    return Promise.reject(new Error('You must need a code'));
  };

  /* get one main-street */
  getOneMainStreet = (address: Pick<IAddress, 'state' | 'city' | 'address1'>): Promise<any> => {
    if (address) {
      return this.handleResponse(
        ApiClientService.get(
          this.getPath(
            `/provinces/${address?.state as string}/municipalities/${address?.city as string}/streets/${
              address?.address1 as string
            }`,
          ),
          addConfig({}),
        ),
      );
    }
    return Promise.reject(new Error('You must need a state, city and code'));
  };

  /* get one main-street */
  getOneStreet = (address: Pick<IAddress, 'state' | 'city' | 'address1' | 'address2'>): Promise<any> => {
    if (address) {
      return this.handleResponse(
        ApiClientService.get(
          this.getPath(
            `/provinces/${address?.state as string}/municipalities/${address?.city as string}/streets/${
              address?.address1 as string
            }/between-streets/${address?.address2 as string}`,
          ),
          addConfig({}),
        ),
      );
    }
    return Promise.reject(new Error('You must need a state, city and code'));
  };

  /* get one main-street */
  getCuLocation = (address: Pick<IAddress, 'state' | 'city' | 'address1' | 'address2'>): Promise<any> => {
    if (address) {
      return this.handleResponse(
        ApiClientService.get(
          this.getPath(
            `/addresses/point/${address?.state as string}${address?.city as string}${address?.address1 as string}${
              address?.address2 as string
            }`,
          ),
          addConfig({}),
        ),
      );
    }
    return Promise.reject(new Error('You must need a state, city and code'));
  };

  getCuLocationState = (address: Pick<IAddress, 'state'>): Promise<any> => {
    if (address) {
      return this.handleResponse(
        ApiClientService.get(this.getPath(`/addresses/province/point/${address?.state as string}`), addConfig({})),
      );
    }

    return Promise.reject(new Error('You must need a state'));
  };

  getCuLocationStateCity = (address: Pick<IAddress, 'state' | 'city'>): Promise<any> => {
    if (address) {
      return this.handleResponse(
        ApiClientService.get(
          this.getPath(`/addresses/municipality/point/${String(address?.state ?? '')}${String(address?.city ?? '')}`),
          addConfig({}),
        ),
      );
    }
    return Promise.reject(new Error('You must need a state and city'));
  };

  getCuLocationStateCityMainStreet = (address: Pick<IAddress, 'state' | 'city' | 'address1'>): Promise<any> => {
    if (address) {
      return this.handleResponse(
        ApiClientService.get(
          this.getPath(`/addresses/street/point/${String(address?.state ?? '')}${String(address?.city ?? '')}${String(address?.address1 ?? '')}`),
          addConfig({}),
        ),
      );
    }
    return Promise.reject(new Error('You must need a state, city and main street'));
  };

  getCuPoi = (lng: number, lat: number): Promise<any> => {
    if (lng && lat) {
      return this.handleResponse(
        ApiClientService.get(this.getPath(`/addresses/point/reverse/${lng}/${lat}`), addConfig({})),
      );
    }
    return Promise.reject(new Error('You must need a longitude and latitude'));
  };

  getCuLocationByReference = (reference: string): Promise<any> => {
    if (reference) {
      return this.handleResponse(ApiClientService.get(this.getPath(`/addresses/${reference}`), addConfig({})));
    }
    return Promise.reject(new Error('You must need a longitude and latitude'));
  };
}

export default new AddressService(MS_LOCATION_CONFIG.url);
